export enum ProcessExecutionStatus {
  ASSIGNED = 'ASSIGNED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  SKIPPED = 'SKIPPED'
}

export enum StepExecutionStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED',
  FAILED = 'FAILED'
}

export interface ProcessSnapshot {
  name: string;
  goal: string;
  category?: string;
  [key: string]: any;
}

export interface StepSnapshot {
  name: string;
  stepType: string;
  notes?: string;
  stepData?: any;
  [key: string]: any;
}

export interface ProcessExecution {
  processExecutionId: string;
  processId: string;
  organizationId: string;
  startedBy: string;
  processSnapshot: ProcessSnapshot;
  status: ProcessExecutionStatus;
  startedAt: string;
  completedAt?: string | null;
  globalNotes?: string | null;
  metadata?: Record<string, any> | null;
  stepExecutions?: StepExecution[];
  process?: {
    processId: string;
    name: string;
    summary?: string;
    goal?: string;
    service?: {
      serviceId: string;
      name: string;
      area?: {
        areaId: string;
        name: string;
      };
    };
  };
  startedByUser?: {
    userId: string;
    displayName: string | null;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  assignedBy?: string | null;
  assignedByUser?: {
    userId: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  assignerNote?: string | null;
  createdAt?: string;
  updatedAt?: string;
  isRedacted?: boolean;
}

export interface StepExecution {
  stepExecutionId: string;
  processExecutionId: string;
  stepId?: string | null;
  executedBy?: string | null;
  organizationId: string;
  stepSnapshot: StepSnapshot;
  sequenceOrder: number;
  status: StepExecutionStatus;
  startedAt?: string | null;
  completedAt?: string | null;
  notes?: string | null;
  errorDetails?: Record<string, any> | null;
  artifacts?: Record<string, any> | null;
  confirmationResponse?: string | null;
  // Substep execution fields
  parentStepExecutionId?: string | null;
  iterationNumber?: number | null;
  substepOrder?: number | null;
  // Container step executions have their substeps nested here
  childStepExecutions?: StepExecution[];
  step?: {
    stepId: string;
    name: string;
    stepType: string;
    stepData?: any;
  };
  executedByUser?: {
    userId: string;
    displayName: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface ProcessExecutionCreateInput {
  processId: string;
  processSnapshot: ProcessSnapshot;
  globalNotes?: string;
  metadata?: Record<string, any>;
}

export interface ProcessExecutionUpdateInput {
  processExecutionId: string;
  status?: ProcessExecutionStatus;
  globalNotes?: string;
  metadata?: Record<string, any>;
  completedAt?: string;
}

export interface StepExecutionCreateInput {
  processExecutionId: string;
  stepId?: string;
  stepSnapshot: StepSnapshot;
  sequenceOrder: number;
  notes?: string;
  executedBy?: string;
}

export interface StepExecutionUpdateInput {
  stepExecutionId: string;
  status?: StepExecutionStatus;
  notes?: string;
  errorDetails?: Record<string, any>;
  artifacts?: Record<string, any>;
  confirmationResponse?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface startProcessExecutionRequest {
  startProcessExecution: ProcessExecution & {
    resumedFrom?: 'IN_PROGRESS' | 'ASSIGNED' | null;
    stepExecutions?: StepExecution[];
  };
}

export interface updateProcessExecutionRequest {
  updateProcessExecution: ProcessExecution;
}

export interface completeProcessExecutionRequest {
  completeProcessExecution: ProcessExecution;
}

export interface createStepExecutionRequest {
  createStepExecution: StepExecution;
}

export interface updateStepExecutionRequest {
  updateStepExecution: StepExecution;
}

export interface updateStepExecutionStatusRequest {
  updateStepExecutionStatus: StepExecution;
}

export interface getProcessExecutionsRequest {
  getProcessExecutions: ProcessExecution[];
}

export interface getLatestExecutionHistoryRequest {
  getLatestExecutionHistory: ProcessExecution[];
}

export interface PaginationInput {
  limit: number;
  offset: number;
}

export interface PageInfo {
  totalCount: number;
  count: number;
  offset: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginatedProcessExecutionResponse {
  items: ProcessExecution[];
  pageInfo: PageInfo;
}

export interface getExecutionHistoryPaginatedRequest {
  getExecutionHistoryPaginated: PaginatedProcessExecutionResponse;
}

export interface getExecutionWithStepsRequest {
  getExecutionWithSteps: ProcessExecution;
}

export interface getStepExecutionsByProcessExecutionRequest {
  getStepExecutionsByProcessExecution: StepExecution[];
}

export interface getMyExecutionsRequest {
  getMyExecutions: PaginatedProcessExecutionResponse;
}

export interface getAssignedByMeRequest {
  getAssignedByMe: PaginatedProcessExecutionResponse;
}

export interface getMyPendingExecutionsCountRequest {
  getMyPendingExecutionsCount: number;
}

export interface getMyPendingExecutionsRequest {
  getMyPendingExecutions: PaginatedProcessExecutionResponse;
}

export interface GoBackToPreviousStepResponse {
  stepExecutionId: string;
  sequenceOrder: number;
  status: StepExecutionStatus;
  step?: {
    stepId: string;
    name: string;
  };
}

export interface goBackToPreviousStepRequest {
  goBackToPreviousStep: GoBackToPreviousStepResponse | null;
}

export type ExecutionGroupingMode = 'process' | 'user';

export type DateFilterPreset = 'today' | 'week' | 'month' | 'custom' | 'all';

export interface SelectedUser {
  email: string;
  displayName: string;
}

export interface SelectedProcess {
  processId: string;
  name: string;
}

export interface SelectedArea {
  areaId: string;
  name: string;
}

export interface SelectedService {
  serviceId: string;
  name: string;
}

export interface ExecutionFilterState {
  selectedUsers: SelectedUser[];
  selectedProcesses: SelectedProcess[];
  selectedAreas: SelectedArea[];
  selectedServices: SelectedService[];
  selectedStatuses: ProcessExecutionStatus[];
  groupingMode: ExecutionGroupingMode;
  dateFilter?: DateFilterPreset;
  customDateFrom?: string;
  customDateTo?: string;
}

// Substep execution types
export interface getSubstepExecutionsRequest {
  getSubstepExecutions: StepExecution[];
}

export interface getSubstepExecutionsByIterationRequest {
  getSubstepExecutionsByIteration: StepExecution[];
}

export interface getCurrentIterationNumberRequest {
  getCurrentIterationNumber: number;
}

export interface initializeSwitchSubstepsRequest {
  initializeSwitchSubsteps: StepExecution[];
}

export interface startNewIterationRequest {
  startNewIteration: StepExecution[];
}
